import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stack,
  Typography,
  IconButton,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import EditIcon from "@mui/icons-material/EditOutlined";
import ChatIcon from "@mui/icons-material/ChatOutlined";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import { addUpdate, editUpdate } from "./ticketsSlice";
import FileGallery from "./FileGallery";

const fieldSx = { "& .MuiInputBase-input": { fontSize: "0.8125rem" } };

export default function TicketUpdateDialog({
  open,
  onClose,
  ticketId,
  editingUpdate,
}) {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { saving } = useSelector((s) => s.tickets);
  const isEdit = Boolean(editingUpdate);

  const [text, setText] = useState("");
  const [existing, setExisting] = useState([]);
  const [removed, setRemoved] = useState([]);
  const [newFiles, setNewFiles] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (editingUpdate) {
      setText(editingUpdate.text || "");
      setExisting(editingUpdate.files || []);
      setRemoved([]);
      setNewFiles([]);
    } else {
      setText("");
      setExisting([]);
      setRemoved([]);
      setNewFiles([]);
    }
  }, [open, editingUpdate]);

  const submit = async (e) => {
    e?.preventDefault();
    if (!text.trim() && !newFiles.length && !removed.length) {
      toast.error("Add text, files, or remove an existing file");
      return;
    }

    const fd = new FormData();
    fd.append("text", text);
    newFiles.forEach((f) => fd.append("files", f));

    let res;
    if (isEdit) {
      fd.append("removedFiles", JSON.stringify(removed));
      res = await dispatch(
        editUpdate({ id: ticketId, updateId: editingUpdate._id, formData: fd })
      );
    } else {
      res = await dispatch(addUpdate({ id: ticketId, formData: fd }));
    }
    const fulfilled = isEdit
      ? editUpdate.fulfilled.match(res)
      : addUpdate.fulfilled.match(res);
    if (fulfilled) {
      toast.success(isEdit ? "Update saved" : "Update added");
      onClose();
    } else {
      toast.error(res.payload || "Save failed");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isMobile}
      // The parent TicketDetailDrawer is rendered at zIndex `modal + 1` so a
      // default Dialog (zIndex = `modal`) would render behind it. Push this
      // dialog (and its backdrop) above the drawer so the loading state and
      // form are visible.
      sx={{ zIndex: (t) => t.zIndex.modal + 2 }}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : "14px",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          px: 2,
          pt: 2.5,
          pb: 2,
          background:
            "linear-gradient(135deg, #0d9488 0%, #14b8a6 50%, #2dd4bf 100%)",
          color: "common.white",
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={saving}
          size="small"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: "common.white",
            backgroundColor: "rgba(255,255,255,0.15)",
            "&:hover": { backgroundColor: "rgba(255,255,255,0.25)" },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.22)",
              border: "2px solid rgba(255,255,255,0.35)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isEdit ? <EditIcon /> : <ChatIcon />}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontWeight: 700,
                fontSize: "1rem",
                letterSpacing: "-0.01em",
                lineHeight: 1.15,
              }}
            >
              {isEdit ? "Edit Update" : "Add Update"}
            </Typography>
            <Typography
              sx={{ fontSize: "0.75rem", opacity: 0.92, mt: 0.25, pr: 4 }}
            >
              {isEdit
                ? "Edit the note or attachments on this update"
                : "Add a note or attach files to this ticket"}
            </Typography>
          </Box>
        </Stack>
      </Box>
      <form onSubmit={submit}>
        <DialogContent sx={{ p: 1.5 }}>
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={3}
            label="Note"
            value={text}
            onChange={(e) => setText(e.target.value)}
            sx={{ ...fieldSx, mb: 1.5 }}
          />
          <FileGallery
            existing={existing}
            newFiles={newFiles}
            addFiles={(arr) => setNewFiles((p) => [...p, ...arr])}
            removeExisting={(name) => {
              setRemoved((p) => [...p, name]);
              setExisting((p) => p.filter((f) => f !== name));
            }}
            removeNew={(i) => setNewFiles((p) => p.filter((_, idx) => idx !== i))}
          />
        </DialogContent>
        <DialogActions
          sx={{
            p: 1.5,
            borderTop: "1px solid",
            borderColor: "divider",
            backgroundColor: "background.paper",
            gap: 1,
            justifyContent: { xs: "stretch", sm: "flex-end" },
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={saving}
            sx={{ borderRadius: "8px", flex: { xs: 1, sm: "0 0 auto" } }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={saving}
            startIcon={
              saving ? <CircularProgress size={14} color="inherit" /> : null
            }
            sx={{
              borderRadius: "8px",
              flex: { xs: 1, sm: "0 0 auto" },
              boxShadow: "0 4px 10px -4px rgba(13,148,136,0.55)",
            }}
          >
            {saving ? "Saving…" : isEdit ? "Save" : "Add"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
